import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';

const CART_KEY = 'smb_cart';

const CartContext = createContext(null);

function loadCart() {
  try {
    const raw = localStorage.getItem(CART_KEY);
    return raw ? JSON.parse(raw) : [];
  } catch {
    return [];
  }
}

function saveCart(cart) {
  try {
    localStorage.setItem(CART_KEY, JSON.stringify(cart));
  } catch {
    // ignore storage errors
  }
}

export function CartProvider({ children }) {
  const [cart, setCartState] = useState(loadCart);

  const setCart = useCallback((updater) => {
    setCartState((prev) => {
      const next = typeof updater === 'function' ? updater(prev) : updater;
      saveCart(next);
      return next;
    });
  }, []);

  // sync across tabs
  useEffect(() => {
    function onStorage(e) {
      if (e.key === CART_KEY) {
        setCartState(loadCart());
      }
    }
    window.addEventListener('storage', onStorage);
    return () => window.removeEventListener('storage', onStorage);
  }, []);

  const addToCart = useCallback((product, qty = 1) => {
    setCart((prev) => {
      const idx = prev.findIndex((i) => i.productId === product.id);
      if (idx >= 0) {
        const updated = [...prev];
        updated[idx] = { ...updated[idx], quantity: updated[idx].quantity + qty };
        return updated;
      }
      return [
        ...prev,
        {
          productId: product.id,
          sellerId: product.seller_id || product.sellerId || product.seller?.id || null,
          name: product.name,
          price: product.price,
          unit: product.unit || 'piece',
          quantity: qty,
          sellerName: product.seller_name || product.sellerName || product.seller?.businessName || '',
          city: product.city || product.seller?.city || '',
          imageUrl: product.image_url || product.imageUrl || '',
          currency: product.currency || 'INR',
          courierCharge: product.courier_charge ? Number(product.courier_charge) : 0,
        },
      ];
    });
  }, [setCart]);

  const removeFromCart = useCallback((productId) => {
    setCart((prev) => prev.filter((i) => i.productId !== productId));
  }, [setCart]);

  const updateQty = useCallback((productId, qty) => {
    if (qty <= 0) {
      setCart((prev) => prev.filter((i) => i.productId !== productId));
    } else {
      setCart((prev) =>
        prev.map((i) => (i.productId === productId ? { ...i, quantity: qty } : i))
      );
    }
  }, [setCart]);

  const clearCart = useCallback(() => {
    setCart([]);
  }, [setCart]);

  const cartCount = cart.reduce((sum, i) => sum + i.quantity, 0);
  const cartTotal = cart.reduce((sum, i) => sum + i.price * i.quantity, 0);

  // One courier charge per seller (use max courier_charge among products from same seller)
  const courierBySeller = cart.reduce((acc, i) => {
    const sid = i.sellerId || 'unknown';
    acc[sid] = Math.max(acc[sid] || 0, i.courierCharge || 0);
    return acc;
  }, {});
  const courierTotal = Object.values(courierBySeller).reduce((s, c) => s + c, 0);
  const grandTotal = cartTotal + courierTotal;

  return (
    <CartContext.Provider
      value={{ cart, addToCart, removeFromCart, updateQty, clearCart, cartCount, cartTotal, courierTotal, courierBySeller, grandTotal }}
    >
      {children}
    </CartContext.Provider>
  );
}

export function useCart() {
  const ctx = useContext(CartContext);
  if (!ctx) throw new Error('useCart must be used inside CartProvider');
  return ctx;
}
